import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";

const ImageViewer = ({
  images,
  interval = 3000,
  animation = "fade",
  aspectRatio = 3 / 1,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  // Function to start the interval
  const startImageCycle = () => {
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, interval);
  };

  // Function to stop the interval
  const stopImageCycle = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  useEffect(() => {
    startImageCycle(); // Start cycling images on component mount

    return () => stopImageCycle(); // Cleanup on unmount
  }, [images.length, interval]);

  const getAnimationStyles = (index) => {
    const isVisible = index === currentIndex;

    switch (animation) {
      case "fade":
        return {
          opacity: isVisible ? 1 : 0,
          transition: "opacity 1s ease-in-out",
        };
      case "slide":
        return {
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
          opacity: isVisible ? 1 : 0,
          transition: "transform 1s ease-in-out, opacity 0.5s ease-in-out",
        };
      case "zoom":
        return {
          transform: isVisible ? "scale(1)" : "scale(1.25)",
          opacity: isVisible ? 1 : 0,
          transition: "transform 2s ease-in-out, opacity 1s ease-in-out",
        };
      default:
        return {};
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        aspectRatio: aspectRatio,
        width: "100%",
        borderRadius: "0.5rem",
        marginTop: "2.5rem",
        marginBottom: "2.5rem",
        cursor: "grab",
      }}
      onMouseEnter={stopImageCycle}
      onMouseLeave={startImageCycle}
    >
      {images.map((image, index) => (
        <Box
          key={index}
          component="img"
          src={image}
          alt={`Slide ${index}`}
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            top: 0,
            left: 0,
            filter: "grayscale(95%) blur(0.5px) contrast(110%)",
            ...getAnimationStyles(index),
          }}
        />
      ))}
    </Box>
  );
};

export default ImageViewer;
